import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@services/firebase';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Autocomplete,
  Chip,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { volunteerRegistrationSchema } from '@utils/validators';
import PasswordStrengthIndicator from '@components/auth/PasswordStrengthIndicator';
import { VOLUNTEER_SKILLS } from '@utils/constants';
import { useAuth } from '@hooks/useAuth';
import { getOrganizationById, verifyOrganizationByInviteCode } from '@services/organizationService';
import { getOrgAdminsByOrganization } from '@services/userService';
import { notifyPendingVolunteer } from '@services/notificationService';

const UserRegistrationForm = ({ googleUser = null, defaultInviteCode = '' }) => {
  const navigate = useNavigate();
  const { signup, completeGoogleSignup, refreshUserData } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [organizationName, setOrganizationName] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Create conditional validation schema based on auth method
  const validationSchema = googleUser
    ? volunteerRegistrationSchema.omit(['password', 'confirmPassword'])
    : volunteerRegistrationSchema;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      displayName: googleUser?.displayName || '',
      email: googleUser?.email || '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      organizationId: defaultInviteCode.toUpperCase(),
      skills: [],
    },
  });

  // Update form values when googleUser changes
  useEffect(() => {
    if (googleUser) {
      setValue('displayName', googleUser.displayName || '');
      setValue('email', googleUser.email || '');
    }
  }, [googleUser, setValue]);

  const handleSuccessClose = () => {
    setSuccessDialogOpen(false);
    // Reset form
    setValue('displayName', googleUser?.displayName || '');
    setValue('email', googleUser?.email || '');
    setValue('password', '');
    setValue('confirmPassword', '');
    setValue('phoneNumber', '');
    setValue('organizationId', '');
    setValue('skills', []);
    setError('');
    // Navigate to dashboard
    navigate('/dashboard');
  };

  const onSubmit = async (data) => {
    console.log('🔵 UserRegistrationForm: onSubmit called', { googleUser: !!googleUser, data });
    console.log('🔵 Form errors:', errors);
    try {
      setError('');
      setLoading(true);

      // reCAPTCHA v3 verification
      if (executeRecaptcha) {
        const token = await executeRecaptcha('volunteer_registration');
        const verifyRecaptcha = httpsCallable(functions, 'verifyRecaptcha');
        const result = await verifyRecaptcha({ token });
        if (!result.data.success) {
          setError('Bot check failed. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Check if input is an invite code (8 characters, alphanumeric)
      const isInviteCode = /^[A-Z0-9]{8}$/i.test(data.organizationId.trim());
      console.log('🔵 UserRegistrationForm: isInviteCode =', isInviteCode);

      // Create user account first with a temporary/pending org ID
      // We'll verify and update it after authentication
      const userData = {
        displayName: data.displayName,
        userType: 'volunteer',
        organizationId: data.organizationId.trim(), // Store input temporarily
        organizationName: '', // Will be updated after verification
        phoneNumber: data.phoneNumber || '',
        skills: data.skills || [],
        status: 'pending', // Pending approval from org admin
      };

      if (googleUser) {
        // Complete Google sign-up
        console.log('🔵 UserRegistrationForm: Calling completeGoogleSignup with:', { googleUser, userData });
        await completeGoogleSignup(googleUser, userData);
        console.log('✅ UserRegistrationForm: completeGoogleSignup completed');
      } else {
        // Email/password sign-up
        console.log('🔵 UserRegistrationForm: Calling signup');
        await signup(data.email, data.password, userData);
        console.log('✅ UserRegistrationForm: signup completed');
      }

      // Now that user is authenticated, verify the organization
      let organizationId = data.organizationId.trim();
      let organization = null;

      try {
        if (isInviteCode) {
          // Verify by invite code
          console.log('🔵 Verifying invite code:', organizationId);
          organization = await verifyOrganizationByInviteCode(organizationId);
          console.log('🔵 Organization from invite code:', organization);
          if (!organization) {
            setError('Invalid invite code. Your account has been created but you need a valid invite code. Please contact your organization admin.');
            navigate('/dashboard');
            return;
          }
          organizationId = organization.organizationId;
          console.log('🔵 Extracted organizationId:', organizationId);

          // Update user with correct organization ID
          const { updateUserDocument } = await import('@services/userService');
          const { getCurrentUser } = await import('@services/authService');
          const currentUser = getCurrentUser();

          if (currentUser) {
            await updateUserDocument(currentUser.uid, {
              organizationId: organizationId,
            });
          }
        } else {
          // Verify by organization ID
          organization = await getOrganizationById(organizationId);
        }

        if (organization.status !== 'active') {
          setError('This organization is not currently accepting volunteers. Your account has been created but needs organization approval.');
          navigate('/dashboard');
          return;
        }

        // Update user with organization name
        console.log('🔵 Updating user with organization name:', organization.organizationName);
        const { updateUserDocument } = await import('@services/userService');
        const { getCurrentUser } = await import('@services/authService');
        const currentUser = getCurrentUser();

        if (currentUser) {
          console.log('🔵 Current user UID:', currentUser.uid);
          await updateUserDocument(currentUser.uid, {
            organizationName: organization.organizationName,
          });
          console.log('✅ Organization name updated');

          // Refresh user data in AuthContext so permissions are updated
          console.log('🔵 Refreshing user data in AuthContext...');
          await refreshUserData();
          console.log('✅ User data refreshed');
        } else {
          console.error('❌ No current user found for organization name update');
        }
      } catch (err) {
        console.error('Error verifying organization:', err);
        setError('Could not verify organization. Your account has been created but needs organization verification. Please contact your organization admin.');
      }

      // Notify org admins of pending volunteer
      try {
        const admins = await getOrgAdminsByOrganization(organizationId);
        const adminIds = admins.map((admin) => admin.uid);
        if (adminIds.length > 0) {
          const { getCurrentUser } = await import('@services/authService');
          const currentUser = getCurrentUser();
          await notifyPendingVolunteer(adminIds, data.displayName, currentUser.uid);
        }
      } catch (notifError) {
        console.error('Error sending notifications to admins:', notifError);
        // Don't fail the registration if notifications fail
      }

      // Store organization name and show success dialog
      setOrganizationName(organization?.organizationName || 'Your Organization');
      setSuccessDialogOpen(true);
      console.log('✅ UserRegistrationForm: Registration complete!');
    } catch (err) {
      console.error('❌ UserRegistrationForm error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onInvalid = (errors) => {
    console.error('❌ Form validation errors:', errors);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      sx={{ width: '100%' }}
    >
      <Typography variant="h5" component="h2" gutterBottom>
        Volunteer Registration
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Full Name */}
      <TextField
        fullWidth
        label="Full Name"
        margin="normal"
        {...register('displayName')}
        error={!!errors.displayName}
        helperText={errors.displayName?.message}
        disabled={loading || !!googleUser}
        InputLabelProps={{ shrink: !!googleUser || undefined }}
      />

      {/* Email (disabled if Google user) */}
      <TextField
        fullWidth
        label="Email"
        type="email"
        margin="normal"
        {...register('email')}
        error={!!errors.email}
        helperText={errors.email?.message}
        disabled={loading || !!googleUser}
        InputLabelProps={{ shrink: !!googleUser || undefined }}
      />

      {/* Password fields (only for email/password registration) */}
      {!googleUser && (
        <>
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            margin="normal"
            {...register('password')}
            error={!!errors.password}
            helperText={errors.password?.message}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <PasswordStrengthIndicator password={watch('password')} />

          <TextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            margin="normal"
            {...register('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </>
      )}

      {/* Phone Number */}
      <TextField
        fullWidth
        label="Phone Number (Optional)"
        margin="normal"
        {...register('phoneNumber')}
        error={!!errors.phoneNumber}
        helperText={errors.phoneNumber?.message}
        disabled={loading}
      />

      {/* Organization Invite Code */}
      <TextField
        fullWidth
        label="Organization Invite Code"
        margin="normal"
        {...register('organizationId')}
        error={!!errors.organizationId}
        helperText={
          errors.organizationId?.message ||
          (defaultInviteCode
            ? 'Invite code pre-filled from your invitation link'
            : 'Enter the 8-character invite code from your organization admin')
        }
        disabled={loading || !!defaultInviteCode}
        placeholder="e.g., ABC123XY"
        InputLabelProps={{ shrink: !!defaultInviteCode || undefined }}
        inputProps={{ style: { textTransform: 'uppercase' } }}
      />

      {/* Skills */}
      <Controller
        name="skills"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            multiple
            options={VOLUNTEER_SKILLS}
            value={value || []}
            onChange={(_, newValue) => onChange(newValue)}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Skills (Optional)"
                margin="normal"
                helperText="Select your skills to help match you with events"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
          />
        )}
      />

      {/* Submit Button */}
      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ mt: 3 }}
        onClick={() => console.log('🔵 Create Account button clicked')}
      >
        {loading ? 'Creating Account...' : 'Create Account'}
      </Button>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" sx={{ fontSize: 32 }} />
            Account Created Successfully!
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Welcome! Your volunteer account has been created for <strong>{organizationName}</strong>.
          </DialogContentText>
          <Alert severity="info">
            Your account is pending approval from the organization admin. You'll receive a notification once approved and can then start signing up for events.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSuccessClose}
            variant="contained"
            size="large"
            fullWidth
          >
            Go to Dashboard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserRegistrationForm;
